/**
 * Quarter-aware time utilities for Territory Analytics.
 *
 * Pure helpers (no React, no hidden `Date.now()`): every "current time" input is
 * passed in so the functions stay deterministic and testable. The frontend
 * expands presets / ranges / comparisons into a flat `Period[]` list.
 */

export type Granularity = 'quarter' | 'year';

export type QuarterNum = 1 | 2 | 3 | 4;

/** A single time bucket. `quarter` present ⇒ quarterly; absent ⇒ annual. */
export interface Period {
  year: number;
  quarter?: QuarterNum;
}

/** Presets surfaced in the Time Period dropdown. */
export type TimePreset = 'last4' | 'all' | 'lastCalendarYear' | 'custom';

/** Bounds of available data, used to expand presets and build the Custom grid. */
export interface AvailableRange {
  /** Earliest available quarter. */
  earliest: Period;
  /**
   * Latest data-complete (VRDC) quarter. Drives the "complete" presets (Last N
   * Complete, Last Calendar Year) and the incompleteness marking — excludes the
   * preliminary quarters that sit beyond it.
   */
  latestComplete: Period;
  /** Latest available quarter, including preliminary (not-yet-complete) data. */
  latestAvailable: Period;
  /** Latest year with any data (includes the current, partial year). */
  latestAvailableYear: number;
}

/** The available-data window a quarter picker's presets and Custom grid use. */
export interface AvailableDataRange extends AvailableRange {
  /** All available quarters (earliest → latest available), for the Custom grid. */
  availableQuarters: Period[];
  /** All available years (includes the current, partial year). */
  availableYears: number[];
}

/** A `{min_year, max_year, years, periods}` extent — a category or its `complete` subset. */
interface DataRangeExtent {
  min_year: number | null;
  max_year: number | null;
  years: number[];
  periods: { year: number; quarter: QuarterNum }[];
}

/**
 * One category of the backend `available-data-range` response (`default` or
 * `reimbursement`). `periods` is the authoritative list of available quarters —
 * the latest year is often partial, so never assume four quarters per year.
 *
 * `complete` is the data-complete (VRDC) subset of `periods`; the quarters
 * beyond it are preliminary. Optional so we tolerate older responses that
 * predate the field (we fall back to treating everything as complete).
 */
export interface AvailableDataRangeCategory extends DataRangeExtent {
  granularities: Granularity[];
  complete?: DataRangeExtent;
}

const QUARTERS: QuarterNum[] = [1, 2, 3, 4];

// --- keys & labels ----------------------------------------------------------

/** API / response key: `"2024-Q1"` (quarter) or `"2024"` (year). */
export const getPeriodKey = (period: Period): string =>
  period.quarter ? `${period.year}-Q${period.quarter}` : `${period.year}`;

/** Human label: `"Q1 2024"` (quarter) or `"2024"` (year). */
export const formatPeriodLabel = (period: Period): string =>
  period.quarter ? `Q${period.quarter} ${period.year}` : `${period.year}`;

/** API request entry — only includes `quarter` when quarterly. */
export const toApiPeriod = (
  period: Period
): { year: number; quarter?: QuarterNum } =>
  period.quarter
    ? { year: period.year, quarter: period.quarter }
    : { year: period.year };

// --- absolute-index helpers (internal) --------------------------------------

const toQuarterIndex = (period: Period): number =>
  period.year * 4 + ((period.quarter ?? 1) - 1);

const fromQuarterIndex = (index: number): Period => ({
  year: Math.floor(index / 4),
  quarter: ((index % 4) + 1) as QuarterNum,
});

// --- "current time" derivations ---------------------------------------------

/** The quarter containing `now`. */
export const getCurrentQuarter = (now: Date): Period => ({
  year: now.getFullYear(),
  quarter: (Math.floor(now.getMonth() / 3) + 1) as QuarterNum,
});

/** The most recent fully-complete quarter (the quarter before the current one). */
export const getLatestCompleteQuarter = (now: Date): Period =>
  fromQuarterIndex(toQuarterIndex(getCurrentQuarter(now)) - 1);

/**
 * Last fully-complete calendar year — `latestComplete.year` only when Q4 is
 * complete, otherwise the prior year.
 */
export const lastCompleteCalendarYear = (latestComplete: Period): number =>
  latestComplete.quarter === 4 ? latestComplete.year : latestComplete.year - 1;

// --- range builders ----------------------------------------------------------

/** Inclusive list of quarters from `start` to `end` (chronological). */
export const quartersBetween = (start: Period, end: Period): Period[] => {
  const startIdx = toQuarterIndex({ ...start, quarter: start.quarter ?? 1 });
  const endIdx = toQuarterIndex({ ...end, quarter: end.quarter ?? 4 });
  const periods: Period[] = [];
  for (let idx = startIdx; idx <= endIdx; idx += 1) {
    periods.push(fromQuarterIndex(idx));
  }
  return periods;
};

/** The `n` most recent complete quarters, ending at `latest` (chronological). */
export const lastNCompleteQuarters = (n: number, latest: Period): Period[] => {
  const end = toQuarterIndex(latest);
  const periods: Period[] = [];
  for (let offset = n - 1; offset >= 0; offset -= 1) {
    periods.push(fromQuarterIndex(end - offset));
  }
  return periods;
};

/**
 * Every available quarter, earliest → latest available (includes preliminary
 * quarters, so the "All available" preset offers the full data window).
 */
export const allAvailableQuarters = (range: AvailableRange): Period[] =>
  quartersBetween(range.earliest, range.latestAvailable);

/** The four quarters of `year` (quarter mode) or `[{ year }]` (year mode). */
export const lastCalendarYearPeriods = (
  year: number,
  granularity: Granularity
): Period[] =>
  granularity === 'year'
    ? [{ year }]
    : QUARTERS.map((quarter) => ({ year, quarter }));

// --- preset expansion --------------------------------------------------------

/**
 * Expand a (non-custom) preset into a period list, clamped to the available
 * range. `granularity` decides whether the entries are quarters or years.
 *
 * When the range carries the backend's authoritative `availableQuarters` /
 * `availableYears` lists, "All available" uses them directly so any gap in the
 * backend data is preserved. Bare `AvailableRange` callers (bounds only) fall
 * back to a contiguous reconstruction.
 */
export const expandPreset = (
  preset: Exclude<TimePreset, 'custom'>,
  granularity: Granularity,
  range: AvailableRange &
    Partial<Pick<AvailableDataRange, 'availableQuarters' | 'availableYears'>>
): Period[] => {
  if (granularity === 'year') {
    const endYear = lastCompleteCalendarYear(range.latestComplete);
    let years: number[];
    switch (preset) {
      case 'last4':
        years = [endYear - 3, endYear - 2, endYear - 1, endYear];
        break;
      case 'lastCalendarYear':
        years = [endYear];
        break;
      case 'all':
      default:
        if (range.availableYears) {
          years = range.availableYears;
        } else {
          years = [];
          for (let year = range.earliest.year; year <= range.latestAvailableYear; year += 1) {
            years.push(year);
          }
        }
        break;
    }
    return years
      .filter((year) => year >= range.earliest.year)
      .map((year) => ({ year }));
  }

  let periods: Period[];
  switch (preset) {
    case 'last4':
      periods = lastNCompleteQuarters(4, range.latestComplete);
      break;
    case 'lastCalendarYear':
      periods = lastCalendarYearPeriods(
        lastCompleteCalendarYear(range.latestComplete),
        'quarter'
      );
      break;
    case 'all':
    default:
      periods = range.availableQuarters ?? allAvailableQuarters(range);
      break;
  }
  const earliestIdx = toQuarterIndex(range.earliest);
  return periods.filter((period) => toQuarterIndex(period) >= earliestIdx);
};

// --- back-compat & normalization --------------------------------------------

/** Min/max year (as strings) spanning the periods — bridges legacy gte/lte. */
export const periodsToYearBounds = (
  periods: Period[]
): { gte: string; lte: string } | null => {
  if (!periods.length) return null;
  const years = periods.map((period) => period.year);
  return { gte: String(Math.min(...years)), lte: String(Math.max(...years)) };
};

/** Dedupe by key, sort chronologically, and cap to the most recent `cap`. */
export const dedupeAndCapPeriods = (
  periods: Period[],
  cap = 40
): Period[] => {
  const seen = new Set<string>();
  const unique = periods.filter((period) => {
    const key = getPeriodKey(period);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  unique.sort((left, right) => toQuarterIndex(left) - toQuarterIndex(right));
  return unique.length > cap ? unique.slice(unique.length - cap) : unique;
};

/**
 * Map a backend `available-data-range` category onto the `AvailableDataRange`
 * shape the pickers consume. The window is derived from the authoritative
 * `periods` list (not expanded years × 4, so a partial latest year is honored).
 *
 * Returns `undefined` when the category is missing or empty (still loading, or
 * a data outage returned empty `periods` / null bounds) so callers can render a
 * loading state rather than a fabricated window.
 */
export const mapCategoryToDataRange = (
  category: AvailableDataRangeCategory | undefined
): AvailableDataRange | undefined => {
  if (
    !category ||
    !category.periods?.length ||
    category.min_year == null ||
    category.max_year == null
  ) {
    return undefined;
  }

  const availableQuarters = dedupeAndCapPeriods(
    category.periods,
    category.periods.length
  );
  const earliest = availableQuarters[0];
  const latestAvailable = availableQuarters[availableQuarters.length - 1];

  // The data-complete (VRDC) subset drives the "complete" presets + the
  // incompleteness marking. Three cases:
  //  - `complete` omitted (legacy response) → treat the whole range as complete.
  //  - `complete` present & non-empty → its last quarter is the boundary.
  //  - `complete` present but empty → nothing is data-complete yet, so put the
  //    boundary just before `earliest`: every available quarter is then marked
  //    incomplete and the "complete" presets yield nothing.
  let latestComplete: Period;
  if (!category.complete) {
    latestComplete = latestAvailable;
  } else if (category.complete.periods?.length) {
    const sortedComplete = dedupeAndCapPeriods(
      category.complete.periods,
      category.complete.periods.length
    );
    latestComplete = sortedComplete[sortedComplete.length - 1];
  } else {
    latestComplete = fromQuarterIndex(toQuarterIndex(earliest) - 1);
  }

  let availableYears = category.years;
  if (!availableYears?.length) {
    availableYears = [];
    for (let year = category.min_year; year <= category.max_year; year += 1) {
      availableYears.push(year);
    }
  }

  return {
    earliest,
    latestComplete,
    latestAvailable,
    latestAvailableYear: category.max_year,
    availableQuarters,
    availableYears,
  };
};

/**
 * Whether `period` is still incomplete (data may still be populating) relative
 * to the latest complete quarter — i.e. a quarter after `latestComplete`, or a
 * year past the last fully-complete calendar year.
 */
export const isPeriodIncomplete = (
  period: Period,
  latestComplete: Period
): boolean =>
  period.quarter
    ? toQuarterIndex(period) > toQuarterIndex(latestComplete)
    : period.year > lastCompleteCalendarYear(latestComplete);
